// v0.9 prestige: at level 100 a player can start again from level 1 (up to 10 times) for a permanent bonus, a star
// and a title. The first real player to reach the top chooses the next area of the map (built in a later update).
import { useState } from 'react';
import { api } from '../api.ts';
import { useGame, fmt, ago } from '../state.tsx';
import { Loading, Panel, useLoad, PlayerLink, PrestigeStar, go } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';
import { play } from '../fx/sound.ts';
import { fx } from '../fx/fx.tsx';

export function PrestigePage() {
  const { me, act } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/prestige'), [me?.level, me?.prestige]);
  const [confirming, setConfirming] = useState(false);
  const [choice, setChoice] = useState('');
  const [note, setNote] = useState('');
  if (!data || !me) return <Loading />;

  const doPrestige = async () => {
    setConfirming(false);
    const r = await act('/api/prestige', {});
    if (!r) return;
    play('prestige');
    fx.stamp(`Prestige ${r.roman}`, `“${r.title}” · +${r.bonusPct}% for good`, 'gold', 1100);
    window.setTimeout(() => fx.coins(30, { x: window.innerWidth / 2, y: window.innerHeight / 2 }), 1200);
    reload();
  };
  const choose = async () => {
    const r = await act('/api/prestige/city', { choice, note });
    if (!r) return;
    play('stamp');
    fx.stamp(r.choice, 'is where the family goes next', 'gold');
    reload();
  };

  const next = data.ladder.find((l: any) => !l.reached);
  const c = data.city;
  return (
    <div>
      <h1 className="page-title">Prestige</h1>
      <div className="prestige-head">
        <div className="prestige-big">{data.rank > 0 ? <PrestigeStar rank={data.rank} size={64} /> : <div className="prestige-none">No star yet</div>}</div>
        <div className="grow">
          <div className="prestige-rank">{data.rank > 0 ? <>Prestige {data.roman}: <span className="gold">{data.ladder[data.rank - 1].title}</span></> : 'Not prestiged yet'}</div>
          <p className="muted" style={{ margin: '4px 0' }}>
            At level {data.maxLevel} you can prestige: start again from level 1 with your cash, items, businesses, turf, mafia,
            achievements and titles kept. Your level, experience and skills go back to the start. Each rank adds a permanent
            +{data.bonusPctPerRank}% on job cash and on experience from jobs and fights, a star by your name and a new title.
            You can do it {data.maxRank} times.
          </p>
          <div className="small">Your bonus now: <b className="good">+{data.bonusPct}%</b>{next ? <> · next rank: <b className="gold">+{next.bonusPct}%</b>, “{next.title}”</> : ' · the highest rank there is'}</div>
        </div>
      </div>

      {data.canPrestige ? (
        <div className="prestige-ready-panel"><Panel title="You made it to the top">
          <p style={{ marginTop: 0 }}>You’re level {data.level}. Give it all up and climb again as <b className="gold">Prestige {next.roman}, “{next.title}”</b>, with <b>+{next.bonusPct}%</b> for good.</p>
          <div className="prestige-keep">
            <div><b className="bad">Starts again</b><ul><li>Level and experience</li><li>Skill points, attack, defense</li><li>Max health, energy and stamina</li><li>Mafia slots bought with skill points (your members stay)</li></ul></div>
            <div><b className="good">You keep</b><ul><li>Cash and bank</li><li>Items, businesses and turf</li><li>Your mafia, achievements and titles</li><li>Job mastery and collections</li></ul></div>
          </div>
          <button className="btn red big" onClick={() => { play('drum'); setConfirming(true); }}>Prestige</button>
        </Panel></div>
      ) : data.rank < data.maxRank ? (
        <Panel title="The road to the top">
          <div className="small">Level <b>{me.level}</b> of {data.maxLevel}</div>
          <div className="xpbar prestige-road"><div style={{ width: `${Math.min(100, (me.level / data.maxLevel) * 100)}%` }} /></div>
        </Panel>
      ) : null}

      <Panel title="The ladder">
        <table className="list prestige-ladder">
          <thead><tr><th></th><th>Rank</th><th>Title</th><th className="right">Bonus</th></tr></thead>
          <tbody>
            {data.ladder.map((l: any) => (
              <tr key={l.rank} className={l.reached ? 'reached' : l.rank === data.rank + 1 ? 'next' : 'locked'}>
                <td style={{ width: 30 }}><PrestigeStar rank={l.rank} size={22} /></td>
                <td>Prestige {l.roman}</td>
                <td>{l.title}{l.rank === data.rank && <span className="small gold"> · you</span>}</td>
                <td className="right">+{l.bonusPct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <div className="city-panel"><Panel title="A new part of the city">
        <div className="city-locked"><span className="locked-tag">Locked</span> Prestiged players will be able to travel somewhere new. It opens in a future update.</div>
        {!c ? (
          <p className="muted">Nobody has reached level {data.maxLevel} yet. The first real player to get there chooses where the family goes next.</p>
        ) : c.choice ? (
          <p><PlayerLink id={c.chooserId} name={c.chooserName} /> got to the top first and chose <b className="gold">{c.choice}</b>{c.note ? <span className="muted"> · “{c.note}”</span> : null}. <span className="small dim">{ago(c.chosenAt)}</span></p>
        ) : c.isMe ? (
          <div className="city-choose">
            <p style={{ marginTop: 0 }}>You were the first in the city to reach level {data.maxLevel}. <b>You choose where the family goes next.</b> Pick a place or write your own; you only get one choice.</p>
            <div className="city-ideas">{data.cityIdeas.map((x: string) => <a key={x} className={`city-idea ${choice === x ? 'on' : ''}`} onClick={() => { play('click'); setChoice(x); }}>{x}</a>)}</div>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              <input value={choice} maxLength={40} placeholder="A city or place" onChange={(e) => setChoice(e.target.value)} />
              <input className="grow" value={note} maxLength={300} placeholder="Anything you'd like there (optional)" onChange={(e) => setNote(e.target.value)} />
              <button className="btn" disabled={choice.trim().length < 2} onClick={choose}>Make it so</button>
            </div>
          </div>
        ) : (
          <p><PlayerLink id={c.chooserId} name={c.chooserName} /> got to the top first ({ago(c.reachedAt)}) and is choosing where the family goes next.</p>
        )}
      </Panel></div>

      {data.top.length > 0 && (
        <Panel title="Most prestigious">
          <table className="list">
            <tbody>
              {data.top.map((r: any, i: number) => (
                <tr key={r.id}>
                  <td className="gold" style={{ width: 24 }}><b>{i + 1}</b></td>
                  <td style={{ width: 34 }}><Portrait seed={r.portrait} playerId={r.id} size={26} /></td>
                  <td><PlayerLink id={r.id} name={r.name} /> <PrestigeStar rank={r.prestige} size={15} /></td>
                  <td className="right small">Prestige {r.roman} · level {fmt(r.level)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {confirming && (
        <div className="modal-back" onClick={() => setConfirming(false)}>
          <div className="modal prestige-confirm" role="dialog" aria-label="Confirm prestige" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Start again?</div>
            <div className="modal-body">
              <p>You’ll go back to <b>level 1</b> with your skills reset, and become <b className="gold">Prestige {next.roman}, “{next.title}”</b> with +{next.bonusPct}% on job cash and experience for good. Your cash, items, businesses and turf stay with you.</p>
              <p className="small muted">This can’t be undone.</p>
              <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn gray" onClick={() => setConfirming(false)}>Not yet</button>
                <button className="btn red" onClick={doPrestige}>Prestige {next.roman}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <p className="small dim"><a onClick={() => go(`profile/${me.id}`)}>Back to your profile</a></p>
    </div>
  );
}
